import {
  useEffect,
  useRef,
  useState,
} from "react";

import toast
  from "react-hot-toast";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import DashboardLayout
  from "../../../components/layout/DashboardLayout";

import {
  useCustomerViewModel,
} from "../../../viewmodels/operation/customer/useCustomerViewModel";

import {
  useRepairQuotationViewModel,
} from "../../../viewmodels/operation/repairquotation/useRepairQuotationViewModel";

const EditRepairQuotation =
  () => {

    const { id } =
      useParams();

    const navigate =
      useNavigate();

    const {

      getRepairQuotationById,
      updateRepairQuotation,

    } =
      useRepairQuotationViewModel();

    const {
      customers,
      setSearch,
    } =
      useCustomerViewModel();

    const [
      showCustomerDropdown,
      setShowCustomerDropdown,
    ] =
      useState(false);

    const customerDropdownRef =
      useRef<any>(null);

    const [
      form,
      setForm,
    ] =
      useState<any>(
        null
      );

    const [
      lineItems,
      setLineItems,
    ] =
      useState<any[]>([]);

    // ================= OUTSIDE CLICK =================
    useEffect(() => {

      const handleClickOutside =
        (
          event: any
        ) => {

          if (
            customerDropdownRef.current &&
            !customerDropdownRef.current.contains(
              event.target
            )
          ) {

            setShowCustomerDropdown(
              false
            );

          }

        };

      document.addEventListener(
        "mousedown",
        handleClickOutside
      );

      return () => {

        document.removeEventListener(
          "mousedown",
          handleClickOutside
        );

      };

    }, []);

    // ================= LOAD =================
    const loadData =
      async () => {

        if (!id)
          return;

        const data =
          await getRepairQuotationById(
            id
          );

        if (
          data
        ) {

          setForm({

            ...data,

            date:
              data.date
                ?.split(
                  "T"
                )[0],

          });

          setLineItems(
            data.lineItems ||
              []
          );

        }

      };

    useEffect(() => {

      loadData();

    }, [id]);

    // ================= SELECT CUSTOMER =================
    const handleSelectCustomer =
      (
        customer: any
      ) => {

        const firstAddress =
          customer
            ?.addresses?.[0];

        const fullAddress =
          [
            firstAddress?.addressLine1,
            firstAddress?.addressLine2,
            firstAddress?.city,
            firstAddress?.state,
            firstAddress?.postalCode,
          ]
            .filter(Boolean)
            .join(", ");

        const firstPhone =
          customer
            ?.customerPhoneNumbers?.[0];

        setForm({

          ...form,

          customerName:
            customer.name ||
            "",

          customerEmail:
            customer.email ||
            "",

          contactPerson:
            firstPhone?.name ||
            "",

          contactPhoneNumber:
            firstPhone?.phoneNumber ||
            "",

          customerAddress:
            fullAddress,

        });

        setShowCustomerDropdown(
          false
        );

      };

    if (!form)
      return null;

    // ================= TOTALS =================
    const subTotal =
      lineItems.reduce(
        (
          sum,
          x
        ) =>
          sum +
          Number(
            x.amount || 0
          ),
        0
      );

    const gst =
      (
        subTotal *
        Number(
          form.gstPercentage || 0
        )
      ) / 100;

    const total =
      subTotal + gst;

    // ================= ADD ROW =================
    const addRow =
      () => {

        setLineItems([

          ...lineItems,

          {

            id: crypto.randomUUID(),

            description:
              "",

            amount: "",

          },

        ]);

      };

    // ================= REMOVE ROW =================
    const removeRow =
      (
        index: number
      ) => {

        const updated =
          [...lineItems];

        updated.splice(
          index,
          1
        );

        setLineItems(
          updated
        );

      };

    // ================= SAVE =================
    const handleSubmit =
      async (
        e: any
      ) => {

        e.preventDefault();

        if (
          lineItems.length ===
          0
        ) {

          toast.error(
            "Add at least one line item"
          );

          return;

        }

        const payload = {

          id:
            form.id,

          serviceCallId:
            form.serviceCallId,

          customerName:
            form.customerName,

          customerEmail:
            form.customerEmail,

          contactPerson:
            form.contactPerson,

          contactPhoneNumber:
            form.contactPhoneNumber,

          customerAddress:
            form.customerAddress,

          make:
            form.make,

          model:
            form.model,

          serialNumber:
            form.serialNumber,

          capacity:
            form.capacity,

          no:
            Number(
              form.no || 0
            ),

          orderNo:
            form.orderNo,

          faultDescription:
            form.faultDescription,

          date:
            form.date
              ? new Date(
                  form.date
                ).toISOString()
              : null,

          subTotal,
          gstPercentage:
            Number(
              form.gstPercentage || 0
            ),

          gst,
          total,

          lineItems:
            lineItems.map(
              (
                x
              ) => ({

                id:
                  x.id,

                description:
                  x.description,

                amount:
                  Number(
                    x.amount || 0
                  ),

              })
            ),

        };

        const success =
          await updateRepairQuotation(
            payload
          );

        if (
          success
        ) {

          navigate(
            "/repair-quotations"
          );

        }

      };

    return (

      <DashboardLayout>

        <div className="p-4 md:p-6 max-w-7xl mx-auto">

          <h1 className="text-4xl font-bold mb-8">
            Edit Repair Quotation
          </h1>

          <form
            onSubmit={
              handleSubmit
            }
            className="space-y-8"
          >

            {/* ================= CUSTOMER DETAILS ================= */}
            <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">

              <h2 className="text-2xl font-bold mb-8">
                Customer Details
              </h2>

              <div className="space-y-5">

                {/* CUSTOMER NAME */}
                <div
                  className="relative"
                  ref={
                    customerDropdownRef
                  }
                >

                  <label className="block text-[15px] font-semibold mb-2">
                    Customer Name
                  </label>

                  <input
                    value={
                      form.customerName
                    }
                    onFocus={() =>
                      setShowCustomerDropdown(
                        true
                      )
                    }
                    onChange={(e) => {

                      setForm({

                        ...form,

                        customerName:
                          e.target.value,

                      });

                      setSearch(
                        e.target.value
                      );

                      setShowCustomerDropdown(
                        true
                      );

                    }}
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4 text-[16px]"
                  />

                  {showCustomerDropdown && (

                    <div className="absolute z-50 bg-white border rounded-2xl shadow-xl w-full mt-2 max-h-72 overflow-auto">

                      <button
                        type="button"
                        onClick={() =>
                          setShowCustomerDropdown(
                            false
                          )
                        }
                        className="w-full text-left px-4 py-4 bg-primary text-white font-semibold"
                      >
                        Manual Entry
                      </button>

                      {customers.map(
                        (
                          x: any
                        ) => (

                          <button
                            key={x.id}
                            type="button"
                            onClick={() =>
                              handleSelectCustomer(
                                x
                              )
                            }
                            className="w-full text-left px-4 py-4 border-b hover:bg-slate-100"
                          >
                            {x.name}
                          </button>

                        )
                      )}

                    </div>

                  )}

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                  {/* EMAIL */}
                  <div>

                    <label className="block text-[15px] font-semibold mb-2">
                      Customer Email
                    </label>

                    <input
                      value={
                        form.customerEmail
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          customerEmail:
                            e.target.value,
                        })
                      }
                      className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                    />

                  </div>

                  {/* CONTACT PERSON */}
                  <div>

                    <label className="block text-[15px] font-semibold mb-2">
                      Contact Person
                    </label>

                    <input
                      value={
                        form.contactPerson
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          contactPerson:
                            e.target.value,
                        })
                      }
                      className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                    />

                  </div>

                  {/* PHONE */}
                  <div>

                    <label className="block text-[15px] font-semibold mb-2">
                      Contact Phone Number
                    </label>

                    <input
                      value={
                        form.contactPhoneNumber
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          contactPhoneNumber:
                            e.target.value,
                        })
                      }
                      className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                    />

                  </div>

                </div>

                {/* ADDRESS */}
                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Customer Address
                  </label>

                  <textarea
                    rows={5}
                    value={
                      form.customerAddress
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        customerAddress:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

              </div>

            </div>

            {/* ================= SCALE DETAILS ================= */}
            <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">

              <h2 className="text-2xl font-bold mb-8">
                Scale Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Make
                  </label>

                  <input
                    value={
                      form.make
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        make:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Model
                  </label>

                  <input
                    value={
                      form.model
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        model:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Capacity
                  </label>

                  <input
                    value={
                      form.capacity
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        capacity:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Serial Number
                  </label>

                  <input
                    value={
                      form.serialNumber
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        serialNumber:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

              </div>

            </div>

            {/* ================= QUOTATION DETAILS ================= */}
            <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">

              <h2 className="text-2xl font-bold mb-8">
                Quotation Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Date
                  </label>

                  <input
                    type="date"
                    value={
                      form.date
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        date:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Order No
                  </label>

                  <input
                    value={
                      form.orderNo
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        orderNo:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

                <div>

                  <label className="block text-[15px] font-semibold mb-2">
                    Quotation No
                  </label>

                  <input
                    type="number"
                    value={
                      form.no
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        no:
                          e.target.value,
                      })
                    }
                    className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                  />

                </div>

              </div>

              {/* FAULT */}
              <div>

                <label className="block text-[15px] font-semibold mb-2">
                  Fault Description
                </label>

                <textarea
                  rows={6}
                  value={
                    form.faultDescription
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      faultDescription:
                        e.target.value,
                    })
                  }
                  className="w-full border-2 border-slate-300 rounded-2xl px-5 py-4"
                />

              </div>

            </div>

            {/* ================= LINE ITEM DETAILS ================= */}
            <div className="bg-white border-2 border-slate-200 rounded-3xl p-8">

              <div className="flex justify-between items-center mb-6">

                <h2 className="text-2xl font-bold mb-8">
                  Line Item Details
                </h2>

                <button
                  type="button"
                  onClick={
                    addRow
                  }
                  className="bg-primary text-white px-5 py-3 rounded-2xl font-semibold"
                >
                  + Add Row
                </button>

              </div>

              <table className="w-full border-2 border-slate-300 rounded-2xl overflow-hidden">

                <thead className="bg-slate-100">

                  <tr>

                    <th className="border p-4 text-left text-[15px]">
                      Description
                    </th>

                    <th className="border p-4 w-[180px] text-[15px]">
                      Amount
                    </th>

                    <th className="border p-4 w-[120px] text-[15px]">
                      Action
                    </th>

                  </tr>

                </thead>

                <tbody>

                  {lineItems.map(
                    (
                      item,
                      index
                    ) => (

                      <tr
                        key={
                          item.id
                        }
                      >

                        <td className="border p-3">

                          <textarea
                            rows={3}
                            value={
                              item.description
                            }
                            onChange={(e) => {

                              const updated =
                                [...lineItems];

                              updated[
                                index
                              ].description =
                                e.target.value;

                              setLineItems(
                                updated
                              );

                            }}
                            className="w-full border-2 border-slate-300 rounded-xl px-4 py-3"
                          />

                        </td>

                        <td className="border p-3">

                          <input
                            type="number"
                            value={
                              item.amount
                            }
                            onChange={(e) => {

                              const updated =
                                [...lineItems];

                              updated[
                                index
                              ].amount =
                                e.target.value;

                              setLineItems(
                                updated
                              );

                            }}
                            className="w-full border-2 border-slate-300 rounded-xl px-4 py-3"
                          />

                        </td>

                        <td className="border p-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              removeRow(
                                index
                              )
                            }
                            className="bg-red-500 text-white px-4 py-3 rounded-xl"
                          >
                            Remove
                          </button>

                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

              {/* TOTALS */}
              <div className="flex justify-end mt-8">

                <div className="w-[380px] bg-slate-50 border-2 border-slate-200 rounded-2xl p-6 space-y-4">

                  <div className="flex justify-between text-[16px]">

                    <span className="font-semibold">
                      Sub Total
                    </span>

                    <span>
                      $
                      {subTotal.toFixed(
                        2
                      )}
                    </span>

                  </div>

                  <div className="flex justify-between items-center gap-4">

                    <span className="font-semibold">
                      GST %
                    </span>

                    <input
                      type="number"
                      value={
                        form.gstPercentage
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          gstPercentage:
                            e.target.value,
                        })
                      }
                      className="border-2 border-slate-300 rounded-xl px-4 py-3 w-[130px]"
                    />

                  </div>

                  <div className="flex justify-between text-[16px]">

                    <span className="font-semibold">
                      GST
                    </span>

                    <span>
                      $
                      {gst.toFixed(
                        2
                      )}
                    </span>

                  </div>

                  <div className="flex justify-between text-[26px] font-bold border-t pt-4">

                    <span>
                      Total
                    </span>

                    <span>
                      $
                      {total.toFixed(
                        2
                      )}
                    </span>

                  </div>

                </div>

              </div>

            </div>

            {/* SAVE */}
            <div className="flex justify-end">

              <button
                type="submit"
                 className="px-8 py-3 rounded-2xl bg-primary text-white font-semibold"
              >
                Update Repair Quotation
              </button>

            </div>

          </form>

        </div>

      </DashboardLayout>

    );

  };

export default EditRepairQuotation;